import { DragEvent, FormEvent, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  ChatBubbleLeftIcon,
  CheckIcon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
  EllipsisVerticalIcon,
  EnvelopeIcon,
  EyeIcon,
  FolderIcon,
  FunnelIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  PaperClipIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { DraggableDetailPanel } from '../../components/ui/DraggableDetailPanel';
import { DraggableModalPanel } from '../../components/ui/DraggableModalPanel';
import { FormActionBar } from '../../components/ui/FormActionBar';
import {
  PortalFileFolder,
  PortalFileFolderColor,
  PortalFilesData,
  PortalFileType,
  PortalProjectFile,
} from './portalFilesMock';

type DetailTab = 'details' | 'comments' | 'related';

const folderColorClass: Record<PortalFileFolderColor, string> = {
  blue: 'bg-primary-500',
  red: 'bg-red-500',
  amber: 'bg-amber-500',
  pink: 'bg-pink-500',
  green: 'bg-emerald-500',
  violet: 'bg-violet-500',
  orange: 'bg-orange-500',
  sky: 'bg-sky-500',
  slate: 'bg-secondary-500',
};

const folderColorOptions: { id: PortalFileFolderColor; label: string }[] = [
  { id: 'blue', label: 'Azul' },
  { id: 'red', label: 'Vermelho' },
  { id: 'amber', label: 'Amarelo' },
  { id: 'pink', label: 'Rosa' },
  { id: 'green', label: 'Verde' },
  { id: 'violet', label: 'Roxo' },
  { id: 'orange', label: 'Laranja' },
  { id: 'sky', label: 'Ciano' },
  { id: 'slate', label: 'Cinza' },
];

const fileTypeIconClass: Record<PortalFileType, string> = {
  docx: 'bg-primary-50 text-primary-700',
  pdf: 'bg-red-50 text-red-700',
  fig: 'bg-violet-50 text-violet-700',
  xlsx: 'bg-emerald-50 text-emerald-700',
  folder: 'bg-amber-50 text-amber-700',
  png: 'bg-sky-50 text-sky-700',
  pptx: 'bg-orange-50 text-orange-700',
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

function newId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function resolvePortalFileType(fileName: string): { type: PortalFileType; typeLabel: string } {
  const extension = fileName.includes('.') ? fileName.split('.').pop()?.toLowerCase() ?? '' : '';
  switch (extension) {
    case 'doc':
    case 'docx':
      return { type: 'docx', typeLabel: 'DOCX' };
    case 'pdf':
      return { type: 'pdf', typeLabel: 'PDF' };
    case 'fig':
      return { type: 'fig', typeLabel: 'FIG' };
    case 'xls':
    case 'xlsx':
    case 'csv':
      return { type: 'xlsx', typeLabel: 'XLSX' };
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
    case 'webp':
      return { type: 'png', typeLabel: 'PNG' };
    case 'ppt':
    case 'pptx':
      return { type: 'pptx', typeLabel: 'PPTX' };
    default:
      return { type: 'pdf', typeLabel: extension ? extension.toUpperCase() : 'ARQUIVO' };
  }
}

function fileBaseName(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot <= 0) return fileName;
  return fileName.slice(0, lastDot);
}

type PortalProjectFilesProps = {
  filesData: PortalFilesData;
};

export function PortalProjectFiles({ filesData }: PortalProjectFilesProps) {
  const [folders, setFolders] = useState(filesData.folders);
  const [files, setFiles] = useState(filesData.files);
  const [activeFolderId, setActiveFolderId] = useState('all');
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [selectedId, setSelectedId] = useState<string | null>('file-plan');
  const [detailTab, setDetailTab] = useState<DetailTab>('details');
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [folderColor, setFolderColor] = useState<PortalFileFolderColor>('blue');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [pendingUploads, setPendingUploads] = useState<File[]>([]);
  const [menuFileId, setMenuFileId] = useState<string | null>(null);
  const [renameFileId, setRenameFileId] = useState<string | null>(null);
  const [renameName, setRenameName] = useState('');

  const owners = useMemo(
    () => Array.from(new Set(files.map((file) => file.ownerName))).sort(),
    [files],
  );

  const folderCounts = useMemo(() => {
    const counts: Record<string, number> = { all: files.length };
    for (const folder of folders) {
      if (folder.id === 'all') continue;
      counts[folder.id] = files.filter((file) => file.folderId === folder.id).length;
    }
    return counts;
  }, [files, folders]);

  const filteredFiles = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase('pt-BR');
    return files.filter((file) => {
      if (activeFolderId !== 'all' && file.folderId !== activeFolderId) return false;
      if (typeFilter !== 'all' && file.type !== typeFilter) return false;
      if (ownerFilter !== 'all' && file.ownerName !== ownerFilter) return false;
      if (!normalized) return true;
      const haystack = `${file.name} ${file.typeLabel} ${file.subtitle}`.toLocaleLowerCase('pt-BR');
      return haystack.includes(normalized);
    });
  }, [activeFolderId, files, ownerFilter, query, typeFilter]);

  const selected = files.find((file) => file.id === selectedId) ?? null;
  const renameTarget = files.find((file) => file.id === renameFileId) ?? null;

  function openFile(url: string) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  function createFolder(event: FormEvent) {
    event.preventDefault();
    const name = folderName.trim();
    if (!name) return;
    const folder: PortalFileFolder = {
      id: newId('folder'),
      name,
      color: folderColor,
    };
    setFolders((current) => [...current, folder]);
    setActiveFolderId(folder.id);
    setFolderModalOpen(false);
    setFolderName('');
    setFolderColor('blue');
  }

  function updateSelected(updater: (file: PortalProjectFile) => PortalProjectFile) {
    if (!selectedId) return;
    setFiles((current) => current.map((file) => (file.id === selectedId ? updater(file) : file)));
  }

  function downloadFile(file: PortalProjectFile) {
    setMenuFileId(null);
    openFile(file.downloadUrl);
  }

  function openRename(file: PortalProjectFile) {
    setMenuFileId(null);
    setRenameFileId(file.id);
    setRenameName(file.name);
  }

  function submitRename(event: FormEvent) {
    event.preventDefault();
    const name = renameName.trim();
    if (!renameFileId || !name) return;
    setFiles((current) =>
      current.map((file) => (file.id === renameFileId ? { ...file, name, updatedAtLabel: 'agora' } : file)),
    );
    setRenameFileId(null);
    setRenameName('');
  }

  function duplicateFile(file: PortalProjectFile) {
    setMenuFileId(null);
    const copy: PortalProjectFile = {
      ...file,
      id: newId('file'),
      name: `${file.name} (cópia)`,
      updatedAtLabel: 'agora',
      relatedFileIds: [...file.relatedFileIds],
      comments: file.comments.map((comment) => ({
        ...comment,
        id: newId('comment'),
        replies: comment.replies.map((reply) => ({ ...reply, id: newId('reply') })),
      })),
    };
    setFiles((current) => [copy, ...current]);
    setSelectedId(copy.id);
    setDetailTab('details');
  }

  function deleteFile(file: PortalProjectFile) {
    setMenuFileId(null);
    setFiles((current) =>
      current
        .filter((item) => item.id !== file.id)
        .map((item) => ({
          ...item,
          relatedFileIds: item.relatedFileIds.filter((id) => id !== file.id),
        })),
    );
    if (selectedId === file.id) setSelectedId(null);
    if (renameFileId === file.id) {
      setRenameFileId(null);
      setRenameName('');
    }
  }

  function openUploadModal() {
    setPendingUploads([]);
    setUploadModalOpen(true);
  }

  function closeUploadModal() {
    setUploadModalOpen(false);
    setPendingUploads([]);
  }

  function addPendingUploads(incoming: FileList | File[]) {
    const next = Array.from(incoming).filter((file) => file.size > 0);
    if (next.length === 0) return;
    setPendingUploads((current) => {
      const existingKeys = new Set(current.map((file) => `${file.name}:${file.size}:${file.lastModified}`));
      const unique = next.filter((file) => !existingKeys.has(`${file.name}:${file.size}:${file.lastModified}`));
      return [...current, ...unique];
    });
  }

  function removePendingUpload(index: number) {
    setPendingUploads((current) => current.filter((_, itemIndex) => itemIndex !== index));
  }

  function submitUploads(event: FormEvent) {
    event.preventDefault();
    if (pendingUploads.length === 0) return;

    const targetFolderId = activeFolderId === 'all' ? 'main' : activeFolderId;
    const todayLabel = new Date().toLocaleDateString('pt-BR');
    const created = pendingUploads.map((file) => {
      const { type, typeLabel } = resolvePortalFileType(file.name);
      const objectUrl = URL.createObjectURL(file);
      return {
        id: newId('file'),
        name: fileBaseName(file.name),
        folderId: targetFolderId,
        type,
        typeLabel,
        versionLabel: 'v1.0',
        updatedAtLabel: 'agora',
        createdAtLabel: todayLabel,
        ownerName: 'Ana Silva',
        sizeLabel: formatFileSize(file.size),
        description: 'Arquivo enviado pelo portal.',
        subtitle: 'Enviado agora',
        statusLabel: 'Disponível',
        permissionsLabel: 'Membros do projeto',
        viewUrl: objectUrl,
        downloadUrl: objectUrl,
        linkedProject: 'Implementação CRM',
        relatedFileIds: [],
        comments: [],
      } satisfies PortalProjectFile;
    });

    setFiles((current) => [...created, ...current]);
    setSelectedId(created[0]?.id ?? null);
    setDetailTab('details');
    closeUploadModal();
  }

  return (
    <div className={`relative space-y-5 ${selected ? 'xl:pr-[24rem]' : ''}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-secondary-900">Arquivos do projeto</h2>
          <p className="mt-1 max-w-2xl text-sm text-secondary-600">
            Consulte os arquivos compartilhados, organize por pastas e acompanhe comentários e vínculos.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setFolderModalOpen(true)}
            className="erp-btn-secondary inline-flex items-center gap-1.5"
          >
            <PlusIcon className="h-4 w-4" aria-hidden />
            Nova pasta
          </button>
          <button
            type="button"
            onClick={openUploadModal}
            className="erp-btn-primary inline-flex items-center gap-1.5"
          >
            <ArrowUpTrayIcon className="h-4 w-4" aria-hidden />
            Enviar arquivos
          </button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[15rem_minmax(0,1fr)]">
        <aside className="rounded-2xl border border-secondary-200 bg-white p-3 shadow-sm">
          <p className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-secondary-500">Pastas</p>
          <ul className="space-y-1">
            {folders.map((folder) => {
              const active = activeFolderId === folder.id;
              const count = folderCounts[folder.id] ?? 0;
              return (
                <li key={folder.id}>
                  <button
                    type="button"
                    onClick={() => setActiveFolderId(folder.id)}
                    className={`flex w-full items-center gap-2 rounded-xl px-2 py-2 text-left text-sm ${
                      active
                        ? 'bg-primary-50 font-semibold text-primary-800'
                        : 'text-secondary-700 hover:bg-secondary-50'
                    }`}
                  >
                    <span className={`h-2.5 w-2.5 shrink-0 rounded-sm ${folderColorClass[folder.color]}`} />
                    <span className="min-w-0 flex-1 truncate">{folder.name}</span>
                    <span className="text-xs text-secondary-400">{count}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        <section className="min-w-0 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[14rem] flex-1">
              <MagnifyingGlassIcon
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400"
                aria-hidden
              />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Buscar arquivos..."
                className="w-full rounded-lg border border-secondary-200 bg-white py-2 pl-9 pr-3 text-sm focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
            </div>
            <select
              value={typeFilter}
              onChange={(event) => setTypeFilter(event.target.value)}
              className="rounded-lg border border-secondary-200 bg-white px-3 py-2 text-sm text-secondary-700"
            >
              <option value="all">Tipo</option>
              <option value="docx">DOCX</option>
              <option value="pdf">PDF</option>
              <option value="xlsx">XLSX</option>
              <option value="fig">FIG</option>
              <option value="png">PNG</option>
            </select>
            <select
              value={ownerFilter}
              onChange={(event) => setOwnerFilter(event.target.value)}
              className="rounded-lg border border-secondary-200 bg-white px-3 py-2 text-sm text-secondary-700"
            >
              <option value="all">Responsável</option>
              {owners.map((owner) => (
                <option key={owner} value={owner}>
                  {owner}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg border border-secondary-200 bg-white px-3 py-2 text-sm font-semibold text-secondary-700"
            >
              <FunnelIcon className="h-4 w-4" aria-hidden />
              Mais filtros
            </button>
          </div>

          <div className="overflow-hidden rounded-2xl border border-secondary-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="border-b border-secondary-200 bg-secondary-50 text-xs font-medium text-secondary-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">Nome</th>
                    <th className="px-3 py-3 font-medium">Tipo</th>
                    <th className="px-3 py-3 font-medium">Versão</th>
                    <th className="px-3 py-3 font-medium">Última atualização</th>
                    <th className="px-3 py-3 font-medium">Responsável</th>
                    <th className="px-3 py-3 font-medium">
                      <span className="sr-only">Ações</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFiles.map((file) => (
                    <tr
                      key={file.id}
                      onClick={() => {
                        setSelectedId(file.id);
                        setDetailTab('details');
                      }}
                      className={`cursor-pointer border-t border-secondary-100 ${
                        selectedId === file.id ? 'bg-primary-50/70' : 'hover:bg-secondary-50'
                      }`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <span
                            className={`flex h-9 w-9 items-center justify-center rounded-xl ${fileTypeIconClass[file.type]}`}
                          >
                            {file.type === 'folder' ? (
                              <FolderIcon className="h-5 w-5" aria-hidden />
                            ) : (
                              <DocumentTextIcon className="h-5 w-5" aria-hidden />
                            )}
                          </span>
                          <div>
                            <p className="font-semibold text-secondary-900">{file.name}</p>
                            <p className="text-xs text-secondary-500">{file.subtitle}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-secondary-600">{file.typeLabel}</td>
                      <td className="px-3 py-3 text-secondary-600">{file.versionLabel}</td>
                      <td className="whitespace-nowrap px-3 py-3 text-secondary-600">{file.updatedAtLabel}</td>
                      <td className="px-3 py-3">
                        <span className="inline-flex items-center gap-2">
                          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-50 text-[10px] font-semibold text-primary-700">
                            {initials(file.ownerName)}
                          </span>
                          <span className="whitespace-nowrap text-secondary-700">{file.ownerName}</span>
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <FileActionsMenu
                          open={menuFileId === file.id}
                          onToggle={() => setMenuFileId((current) => (current === file.id ? null : file.id))}
                          onClose={() => setMenuFileId(null)}
                          onDownload={() => downloadFile(file)}
                          onRename={() => openRename(file)}
                          onDuplicate={() => duplicateFile(file)}
                          onDelete={() => deleteFile(file)}
                          triggerClassName="rounded-lg p-1 text-secondary-400 hover:bg-secondary-100 hover:text-secondary-700"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredFiles.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-secondary-500">Nenhum arquivo encontrado.</p>
              ) : null}
            </div>
          </div>
        </section>
      </div>

      {selected ? (
        <FileDetailDrawer
          file={selected}
          allFiles={files}
          activeTab={detailTab}
          onTabChange={setDetailTab}
          onClose={() => setSelectedId(null)}
          onView={() => openFile(selected.viewUrl)}
          onDownload={() => downloadFile(selected)}
          menuOpen={menuFileId === selected.id}
          onToggleMenu={() => setMenuFileId((current) => (current === selected.id ? null : selected.id))}
          onCloseMenu={() => setMenuFileId(null)}
          onRename={() => openRename(selected)}
          onDuplicate={() => duplicateFile(selected)}
          onDelete={() => deleteFile(selected)}
          onAddComment={(body, replyToId) => {
            updateSelected((file) => addCommentToFile(file, body, replyToId));
          }}
          onSaveRelated={(relatedIds) => {
            updateSelected((file) => ({ ...file, relatedFileIds: relatedIds }));
          }}
        />
      ) : null}

      <DraggableModalPanel
        open={uploadModalOpen}
        title="Enviar arquivos"
        subtitle="Arraste os arquivos para a área abaixo ou selecione no computador."
        onClose={closeUploadModal}
        className="w-full max-w-xl"
        footer={
          <FormActionBar showCancel onCancel={closeUploadModal}>
            <button
              type="submit"
              form="portal-upload-files-form"
              className="erp-btn-primary"
              disabled={pendingUploads.length === 0}
            >
              Enviar {pendingUploads.length > 0 ? `(${pendingUploads.length})` : ''}
            </button>
          </FormActionBar>
        }
      >
        <form id="portal-upload-files-form" onSubmit={submitUploads} className="space-y-4">
          <FileDropZone files={pendingUploads} onAddFiles={addPendingUploads} onRemoveFile={removePendingUpload} />
          <p className="text-xs text-secondary-500">
            Os arquivos serão adicionados à pasta{' '}
            <strong>
              {folders.find((folder) => folder.id === (activeFolderId === 'all' ? 'main' : activeFolderId))?.name ??
                'Documentos principais'}
            </strong>
            .
          </p>
        </form>
      </DraggableModalPanel>

      <DraggableModalPanel
        open={Boolean(renameTarget)}
        title="Renomear arquivo"
        subtitle={renameTarget ? renameTarget.subtitle : undefined}
        onClose={() => {
          setRenameFileId(null);
          setRenameName('');
        }}
        footer={
          <FormActionBar
            showCancel
            onCancel={() => {
              setRenameFileId(null);
              setRenameName('');
            }}
          >
            <button
              type="submit"
              form="portal-rename-file-form"
              className="erp-btn-primary"
              disabled={!renameName.trim()}
            >
              Salvar
            </button>
          </FormActionBar>
        }
      >
        <form id="portal-rename-file-form" onSubmit={submitRename} className="space-y-3">
          <label className="block">
            <span className="erp-label">Nome</span>
            <input
              value={renameName}
              onChange={(event) => setRenameName(event.target.value)}
              required
              autoFocus
              className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            />
          </label>
        </form>
      </DraggableModalPanel>

      <DraggableModalPanel
        open={folderModalOpen}
        title="Nova pasta"
        subtitle="Defina um nome e uma cor para organizar os arquivos."
        onClose={() => setFolderModalOpen(false)}
        footer={
          <FormActionBar showCancel onCancel={() => setFolderModalOpen(false)}>
            <button
              type="submit"
              form="portal-new-folder-form"
              className="erp-btn-primary"
              disabled={!folderName.trim()}
            >
              Criar pasta
            </button>
          </FormActionBar>
        }
      >
        <form id="portal-new-folder-form" onSubmit={createFolder} className="space-y-4">
          <label className="block">
            <span className="erp-label">Nome da pasta</span>
            <input
              value={folderName}
              onChange={(event) => setFolderName(event.target.value)}
              required
              placeholder="Ex.: Homologação"
              className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            />
          </label>
          <fieldset>
            <legend className="erp-label">Cor</legend>
            <div className="mt-2 flex flex-wrap gap-2">
              {folderColorOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setFolderColor(option.id)}
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm ${
                    folderColor === option.id
                      ? 'border-primary-400 bg-primary-50 text-primary-800'
                      : 'border-secondary-200 bg-white text-secondary-700'
                  }`}
                  aria-pressed={folderColor === option.id}
                >
                  <span className={`h-3 w-3 rounded-full ${folderColorClass[option.id]}`} />
                  {option.label}
                </button>
              ))}
            </div>
          </fieldset>
        </form>
      </DraggableModalPanel>
    </div>
  );
}

function FileDropZone({
  files,
  onAddFiles,
  onRemoveFile,
}: {
  files: File[];
  onAddFiles: (incoming: FileList | File[]) => void;
  onRemoveFile: (index: number) => void;
}) {
  const inputId = useId();
  const dragDepth = useRef(0);
  const [dragging, setDragging] = useState(false);

  function hasFiles(event: DragEvent<HTMLDivElement>): boolean {
    return Array.from(event.dataTransfer.types).includes('Files');
  }

  function handleDragEnter(event: DragEvent<HTMLDivElement>) {
    if (!hasFiles(event)) return;
    event.preventDefault();
    dragDepth.current += 1;
    setDragging(true);
  }

  function handleDragOver(event: DragEvent<HTMLDivElement>) {
    if (!hasFiles(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }

  function handleDragLeave(event: DragEvent<HTMLDivElement>) {
    if (!hasFiles(event)) return;
    event.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    if (!hasFiles(event)) return;
    event.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    onAddFiles(event.dataTransfer.files);
  }

  const zoneClass = dragging
    ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
    : 'border-secondary-300 bg-secondary-50/60 hover:bg-secondary-100/80';

  return (
    <div className="space-y-3">
      <div
        className={`relative overflow-hidden rounded-xl border border-dashed transition ${zoneClass}`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <label
          htmlFor={inputId}
          className="flex min-h-[10rem] cursor-pointer flex-col items-center justify-center gap-2 px-4 py-8 text-center"
        >
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-secondary-500 shadow-sm ring-1 ring-secondary-200">
            <ArrowUpTrayIcon className="h-6 w-6" aria-hidden />
          </span>
          <span className="text-sm font-semibold text-secondary-900">
            Arraste os arquivos aqui ou clique para selecionar
          </span>
          <span className="text-xs text-secondary-500">PDF, DOCX, XLSX, PNG, PPTX e outros formatos</span>
        </label>
        {dragging ? (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2 bg-primary-50/95 px-4 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-primary-600 shadow-sm ring-1 ring-primary-200">
              <ArrowUpTrayIcon className="h-6 w-6" aria-hidden />
            </span>
            <span className="text-sm font-semibold text-primary-800">Solte os arquivos aqui</span>
          </div>
        ) : null}
      </div>

      <input
        id={inputId}
        type="file"
        multiple
        className="sr-only"
        onChange={(event) => {
          if (event.target.files) onAddFiles(event.target.files);
          event.target.value = '';
        }}
      />

      {files.length > 0 ? (
        <ul className="max-h-48 space-y-2 overflow-y-auto">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
              className="flex items-center gap-3 rounded-xl border border-secondary-200 bg-white px-3 py-2.5"
            >
              <DocumentTextIcon className="h-5 w-5 shrink-0 text-secondary-500" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-secondary-900">{file.name}</p>
                <p className="text-xs text-secondary-500">{formatFileSize(file.size)}</p>
              </div>
              <button
                type="button"
                onClick={() => onRemoveFile(index)}
                className="rounded-lg p-1.5 text-secondary-400 hover:bg-secondary-50 hover:text-red-600"
                aria-label={`Remover ${file.name}`}
              >
                <TrashIcon className="h-4 w-4" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function FileActionsMenu({
  open,
  onToggle,
  onClose,
  onDownload,
  onRename,
  onDuplicate,
  onDelete,
  align = 'right',
  triggerClassName = 'rounded-lg border border-secondary-200 p-2 text-secondary-500 hover:bg-secondary-50 hover:text-secondary-700',
}: {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onDownload: () => void;
  onRename: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  align?: 'left' | 'right';
  triggerClassName?: string;
}) {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) onClose();
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, open]);

  const items: {
    id: string;
    label: string;
    icon: typeof ArrowDownTrayIcon;
    onClick: () => void;
    danger?: boolean;
  }[] = [
    { id: 'download', label: 'Baixar', icon: ArrowDownTrayIcon, onClick: onDownload },
    { id: 'rename', label: 'Renomear', icon: PencilSquareIcon, onClick: onRename },
    { id: 'duplicate', label: 'Duplicar', icon: DocumentDuplicateIcon, onClick: onDuplicate },
    { id: 'delete', label: 'Excluir', icon: TrashIcon, onClick: onDelete, danger: true },
  ];

  return (
    <div ref={rootRef} className="relative" onClick={(event) => event.stopPropagation()}>
      <button
        type="button"
        className={triggerClassName}
        aria-label="Mais opções"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={onToggle}
      >
        <EllipsisVerticalIcon className="h-5 w-5" aria-hidden />
      </button>
      {open ? (
        <div
          role="menu"
          className={`absolute z-40 mt-1 w-44 overflow-hidden rounded-xl border border-secondary-200 bg-white py-1 shadow-lg ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              onClick={item.onClick}
              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${
                item.danger ? 'text-red-700 hover:bg-red-50' : 'text-secondary-700 hover:bg-secondary-50'
              }`}
            >
              <item.icon className="h-4 w-4 shrink-0" aria-hidden />
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function addCommentToFile(
  file: PortalProjectFile,
  body: string,
  replyToId?: string | null,
): PortalProjectFile {
  const trimmed = body.trim();
  if (!trimmed) return file;
  const comment = {
    id: newId('comment'),
    authorName: 'Ana Silva',
    body: trimmed,
    whenLabel: 'agora',
    replies: [] as PortalProjectFile['comments'],
  };

  if (!replyToId) {
    return { ...file, comments: [comment, ...file.comments] };
  }

  return {
    ...file,
    comments: file.comments.map((item) =>
      item.id === replyToId ? { ...item, replies: [...item.replies, comment] } : item,
    ),
  };
}

function FileDetailDrawer({
  file,
  allFiles,
  activeTab,
  onTabChange,
  onClose,
  onView,
  onDownload,
  menuOpen,
  onToggleMenu,
  onCloseMenu,
  onRename,
  onDuplicate,
  onDelete,
  onAddComment,
  onSaveRelated,
}: {
  file: PortalProjectFile;
  allFiles: PortalProjectFile[];
  activeTab: DetailTab;
  onTabChange: (tab: DetailTab) => void;
  onClose: () => void;
  onView: () => void;
  onDownload: () => void;
  menuOpen: boolean;
  onToggleMenu: () => void;
  onCloseMenu: () => void;
  onRename: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onAddComment: (body: string, replyToId?: string | null) => void;
  onSaveRelated: (relatedIds: string[]) => void;
}) {
  const relatedFiles = allFiles.filter((item) => file.relatedFileIds.includes(item.id));
  const commentCount =
    file.comments.length + file.comments.reduce((acc, item) => acc + item.replies.length, 0);

  return (
    <DraggableDetailPanel
      resetKey={file.id}
      header={
        <div className="flex items-start justify-between gap-3 border-b border-secondary-100 px-4 py-4">
          <div className="flex min-w-0 items-start gap-3">
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${fileTypeIconClass[file.type]}`}>
              <DocumentTextIcon className="h-5 w-5" aria-hidden />
            </span>
            <div className="min-w-0">
              <h3 className="truncate text-base font-semibold text-secondary-900">{file.name}</h3>
              <p className="text-xs text-secondary-500">{file.subtitle}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-semibold text-secondary-700">
                  {file.versionLabel}
                </span>
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                  {file.statusLabel}
                </span>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-secondary-400 hover:bg-secondary-50 hover:text-secondary-700"
            aria-label="Fechar detalhes"
          >
            <XMarkIcon className="h-5 w-5" aria-hidden />
          </button>
        </div>
      }
    >
      <div className="border-b border-secondary-200 px-2">
        <nav className="-mb-px flex gap-1 overflow-x-auto">
          {(
            [
              ['details', 'Detalhes'],
              ['comments', `Comentários (${commentCount})`],
              ['related', `Relacionados (${file.relatedFileIds.length})`],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => onTabChange(id)}
              className={`shrink-0 rounded-none border-b-2 px-3 py-2.5 text-sm font-medium ${
                activeTab === id
                  ? 'border-primary-500 text-primary-700'
                  : 'border-transparent text-secondary-600 hover:text-secondary-900'
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {activeTab === 'details' ? (
          <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
            <section>
              <p className="text-xs font-medium text-secondary-500">Descrição</p>
              <p className="mt-1 text-sm leading-relaxed text-secondary-700">{file.description}</p>
            </section>
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-xs text-secondary-500">Tipo de arquivo</dt>
                <dd className="mt-0.5 font-semibold text-secondary-900">
                  {file.typeLabel} · {file.sizeLabel}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-secondary-500">Responsável</dt>
                <dd className="mt-0.5 flex items-center gap-2 font-semibold text-secondary-900">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-50 text-[10px] text-primary-700">
                    {initials(file.ownerName)}
                  </span>
                  {file.ownerName}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-secondary-500">Última atualização</dt>
                <dd className="mt-0.5 font-semibold text-secondary-900">{file.updatedAtLabel}</dd>
              </div>
              <div>
                <dt className="text-xs text-secondary-500">Criado em</dt>
                <dd className="mt-0.5 font-semibold text-secondary-900">{file.createdAtLabel}</dd>
              </div>
              <div className="col-span-2">
                <dt className="text-xs text-secondary-500">Permissões</dt>
                <dd className="mt-0.5 font-semibold text-secondary-900">{file.permissionsLabel}</dd>
              </div>
            </dl>

            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={onDownload} className="erp-btn-primary flex-1">
                Baixar arquivo
              </button>
              <button
                type="button"
                onClick={onView}
                className="erp-btn-secondary inline-flex flex-1 items-center justify-center gap-1.5"
              >
                <EyeIcon className="h-4 w-4" aria-hidden />
                Visualizar
              </button>
              <FileActionsMenu
                open={menuOpen}
                onToggle={onToggleMenu}
                onClose={onCloseMenu}
                onDownload={onDownload}
                onRename={onRename}
                onDuplicate={onDuplicate}
                onDelete={onDelete}
                align="right"
              />
            </div>

            <section className="rounded-xl border border-secondary-200 bg-secondary-50 px-3 py-3">
              <p className="text-sm font-semibold text-secondary-900">Compartilhamento</p>
              <p className="mt-1 text-sm text-secondary-600">
                Visível para os membros autorizados do projeto.
              </p>
              <button type="button" className="mt-2 text-sm font-semibold text-primary-700 hover:underline">
                Gerenciar permissões
              </button>
            </section>

            <section>
              <p className="text-sm font-semibold text-secondary-900">Vinculações</p>
              <ul className="mt-2 space-y-2 text-sm text-secondary-700">
                <li className="flex items-center gap-2">
                  <LinkIcon className="h-4 w-4 text-secondary-400" aria-hidden />
                  Projeto: {file.linkedProject}
                </li>
                {file.linkedDelivery ? (
                  <li className="flex items-center gap-2">
                    <PaperClipIcon className="h-4 w-4 text-secondary-400" aria-hidden />
                    Entrega: {file.linkedDelivery}
                  </li>
                ) : null}
                {file.linkedActivity ? (
                  <li className="flex items-center gap-2">
                    <ChatBubbleLeftIcon className="h-4 w-4 text-secondary-400" aria-hidden />
                    Atividade: {file.linkedActivity}
                  </li>
                ) : null}
                {relatedFiles[0] ? (
                  <li className="flex items-center gap-2">
                    <EnvelopeIcon className="h-4 w-4 text-secondary-400" aria-hidden />
                    Relacionado a: {relatedFiles[0].name}
                  </li>
                ) : null}
              </ul>
            </section>
          </div>
        ) : null}

        {activeTab === 'comments' ? (
          <CommentsPanel key={file.id} comments={file.comments} onAddComment={onAddComment} />
        ) : null}

        {activeTab === 'related' ? (
          <RelatedPanel
            key={file.id}
            currentFileId={file.id}
            allFiles={allFiles}
            selectedIds={file.relatedFileIds}
            onSave={onSaveRelated}
          />
        ) : null}
      </div>
    </DraggableDetailPanel>
  );
}

function CommentsPanel({
  comments,
  onAddComment,
}: {
  comments: PortalProjectFile['comments'];
  onAddComment: (body: string, replyToId?: string | null) => void;
}) {
  const [draft, setDraft] = useState('');
  const [replyToId, setReplyToId] = useState<string | null>(null);
  const replyTarget = comments.find((item) => item.id === replyToId) ?? null;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!draft.trim()) return;
    onAddComment(draft, replyToId);
    setDraft('');
    setReplyToId(null);
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
        {comments.length === 0 ? (
          <p className="text-sm text-secondary-500">Nenhum comentário neste arquivo.</p>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className="space-y-3">
              <CommentItem
                authorName={comment.authorName}
                body={comment.body}
                whenLabel={comment.whenLabel}
                onReply={() => setReplyToId(comment.id)}
              />
              {comment.replies.map((reply) => (
                <div key={reply.id} className="ml-8">
                  <CommentItem
                    authorName={reply.authorName}
                    body={reply.body}
                    whenLabel={reply.whenLabel}
                  />
                </div>
              ))}
            </div>
          ))
        )}
      </div>
      <form onSubmit={submit} className="border-t border-secondary-100 px-4 py-3">
        {replyTarget ? (
          <div className="mb-2 flex items-center justify-between rounded-lg bg-secondary-50 px-2 py-1.5 text-xs text-secondary-600">
            <span>
              Respondendo a <strong>{replyTarget.authorName}</strong>
            </span>
            <button type="button" onClick={() => setReplyToId(null)} className="font-semibold text-primary-700">
              Cancelar
            </button>
          </div>
        ) : null}
        <label className="sr-only" htmlFor="portal-file-new-comment">
          Novo comentário
        </label>
        <textarea
          id="portal-file-new-comment"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          rows={3}
          placeholder="Novo comentário..."
          className="w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
        />
        <div className="mt-2 flex justify-end">
          <button type="submit" className="erp-btn-primary erp-btn-sm" disabled={!draft.trim()}>
            Publicar
          </button>
        </div>
      </form>
    </div>
  );
}

function CommentItem({
  authorName,
  body,
  whenLabel,
  onReply,
}: {
  authorName: string;
  body: string;
  whenLabel: string;
  onReply?: () => void;
}) {
  return (
    <div className="flex gap-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-50 text-[10px] font-semibold text-primary-700">
        {initials(authorName)}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-secondary-800">
          <span className="font-semibold">{authorName}</span>
          <span className="text-secondary-400"> · {whenLabel}</span>
        </p>
        <p className="mt-1 text-sm text-secondary-700">{body}</p>
        {onReply ? (
          <button
            type="button"
            onClick={onReply}
            className="mt-1 rounded-none text-xs font-semibold text-primary-700 hover:underline"
          >
            Responder
          </button>
        ) : null}
      </div>
    </div>
  );
}

function RelatedPanel({
  currentFileId,
  allFiles,
  selectedIds,
  onSave,
}: {
  currentFileId: string;
  allFiles: PortalProjectFile[];
  selectedIds: string[];
  onSave: (relatedIds: string[]) => void;
}) {
  const candidates = allFiles.filter((file) => file.id !== currentFileId);
  const [draftIds, setDraftIds] = useState<string[]>(selectedIds);
  const [editing, setEditing] = useState(selectedIds.length === 0);
  const savedFiles = allFiles.filter((file) => selectedIds.includes(file.id));

  function toggle(fileId: string) {
    setDraftIds((current) =>
      current.includes(fileId) ? current.filter((id) => id !== fileId) : [...current, fileId],
    );
  }

  function save() {
    onSave(draftIds);
    setEditing(false);
  }

  if (!editing) {
    return (
      <div className="flex min-h-0 flex-1 flex-col px-4 py-4">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-sm font-semibold text-secondary-900">Documentos relacionados</p>
          <button
            type="button"
            onClick={() => {
              setDraftIds(selectedIds);
              setEditing(true);
            }}
            className="text-sm font-semibold text-primary-700 hover:underline"
          >
            Editar
          </button>
        </div>
        {savedFiles.length === 0 ? (
          <p className="text-sm text-secondary-500">Nenhum documento relacionado.</p>
        ) : (
          <ul className="space-y-2 overflow-y-auto">
            {savedFiles.map((file) => (
              <li
                key={file.id}
                className="flex items-center gap-3 rounded-xl border border-secondary-200 bg-secondary-50 px-3 py-2.5"
              >
                <DocumentTextIcon className="h-5 w-5 text-secondary-500" aria-hidden />
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-secondary-900">{file.name}</p>
                  <p className="text-xs text-secondary-500">
                    {file.typeLabel} · {file.versionLabel}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="border-b border-secondary-100 px-4 py-3">
        <p className="text-sm font-semibold text-secondary-900">Selecionar relacionados</p>
        <p className="mt-0.5 text-xs text-secondary-500">
          Marque os documentos do projeto que devem aparecer vinculados a este arquivo.
        </p>
      </div>
      <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto px-3 py-3">
        {candidates.map((file) => {
          const checked = draftIds.includes(file.id);
          return (
            <li key={file.id}>
              <label className="flex cursor-pointer items-center gap-3 rounded-xl px-2 py-2 hover:bg-secondary-50">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(file.id)}
                  className="h-4 w-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                />
                <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${fileTypeIconClass[file.type]}`}>
                  <DocumentTextIcon className="h-4 w-4" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-secondary-900">{file.name}</span>
                  <span className="block text-xs text-secondary-500">{file.typeLabel}</span>
                </span>
                {checked ? <CheckIcon className="h-4 w-4 text-primary-600" aria-hidden /> : null}
              </label>
            </li>
          );
        })}
      </ul>
      <div className="flex justify-end gap-2 border-t border-secondary-100 px-4 py-3">
        <button
          type="button"
          onClick={() => {
            setDraftIds(selectedIds);
            setEditing(false);
          }}
          className="erp-btn-secondary erp-btn-sm"
        >
          Cancelar
        </button>
        <button type="button" onClick={save} className="erp-btn-primary erp-btn-sm">
          Salvar
        </button>
      </div>
    </div>
  );
}
